import { create } from 'zustand'
import {
  CNSLearning,
  CNSLearningAnnotation,
  LearningStatus,
} from '@/models/cns'
import {
  fetchLearnings as fetchLearningsService,
  fetchLearningsByExperiment as fetchLearningsByExperimentService,
  fetchLearning as fetchLearningService,
  deleteLearning as deleteLearningService,
  updateLearning as patchLearningApi,
} from '@/services/learningService'
import { fetchLearningAnnotationForLearning } from '@/services/learningAnnotationService'
import { notifyWorkflowSnapshotStale } from '@/lib/notifyWorkflowSnapshotStale'
import { useProjectDashboardStore } from '@/stores/useProjectDashboardStore'

function mergeAnnotationIntoLearning(
  learning: CNSLearning,
  updated: CNSLearningAnnotation
): CNSLearning {
  const prev = learning.annotation
  return {
    ...learning,
    annotation: prev ? { ...prev, ...updated } : updated,
  }
}

function mapLearningsWithMerged(
  list: CNSLearning[],
  learningId: string,
  merged: CNSLearning
): CNSLearning[] {
  return list.map((l) => (l.id === learningId ? merged : l))
}

interface LearningStore {
  // State
  learnings: CNSLearning[]
  loading: boolean
  error: string | null
  isInitialized: boolean
  // Project-scoped state for deduplication (prevents duplicate API calls)
  learningsByProject: Record<string, CNSLearning[]>
  loadingByProject: Record<string, boolean>
  errorByProject: Record<string, string | null>
  /**
   * Cached annotations by learning id (optional; avoids repeat GET when embedded learning lacks annotation.id).
   * Pair with annotationAbsentByLearningId: 404 on nested GET means no annotation row yet, not an error.
   */
  annotationsByLearningId: Record<string, CNSLearningAnnotation | undefined>
  /** Learning ids for which nested GET returned 404 (no annotation row); skip refetch until merge/create. */
  annotationAbsentByLearningId: Record<string, true>

  // Actions
  fetchLearnings: (projectId: string) => Promise<void>
  fetchLearningsByExperiment: (projectId: string, experimentId: string) => Promise<void>
  fetchLearning: (projectId: string, learningId: string) => Promise<CNSLearning | null>
  addLearning: (learning: CNSLearning) => void
  updateLearning: (learningId: string, updates: Partial<CNSLearning>) => void
  removeLearning: (learningId: string) => void
  updateLearningStatus: (projectId: string, learningId: string, status: LearningStatus) => Promise<CNSLearning>
  deleteLearning: (projectId: string, learningId: string) => Promise<void>
  clearAll: () => void
  mergeLearningAnnotationForLearning: (
    learningId: string,
    annotation: CNSLearningAnnotation
  ) => void
  ensureLearningAnnotationHydrated: (
    projectId: string,
    learningId: string
  ) => Promise<CNSLearningAnnotation | null>

  // Getters
  getLearningById: (id: string) => CNSLearning | undefined
  getLearningsByExperiment: (experimentId: string) => CNSLearning[]
  getLearningsByProject: (projectId: string) => CNSLearning[]
}

export const useLearningStore = create<LearningStore>((set, get) => ({
  // Initial state
  learnings: [],
  loading: false,
  error: null,
  isInitialized: false,
  learningsByProject: {},
  loadingByProject: {},
  errorByProject: {},
  annotationsByLearningId: {},
  annotationAbsentByLearningId: {},

  mergeLearningAnnotationForLearning: (learningId, annotation) => {
    set((state) => {
      const inGlobal = state.learnings.some((l) => l.id === learningId)
      const nextLearnings = inGlobal
        ? state.learnings.map((l) =>
            l.id === learningId ? mergeAnnotationIntoLearning(l, annotation) : l
          )
        : state.learnings
      const nextByProject = { ...state.learningsByProject }
      for (const pid of Object.keys(nextByProject)) {
        const list = nextByProject[pid]
        if (list.some((l) => l.id === learningId)) {
          nextByProject[pid] = list.map((l) =>
            l.id === learningId ? mergeAnnotationIntoLearning(l, annotation) : l
          )
        }
      }
      const nextAbsent = { ...state.annotationAbsentByLearningId }
      delete nextAbsent[learningId]
      return {
        learnings: nextLearnings,
        learningsByProject: nextByProject,
        annotationsByLearningId: {
          ...state.annotationsByLearningId,
          [learningId]: annotation,
        },
        annotationAbsentByLearningId: nextAbsent,
      }
    })
  },

  ensureLearningAnnotationHydrated: async (projectId, learningId) => {
    const { annotationsByLearningId, annotationAbsentByLearningId } = get()
    if (annotationAbsentByLearningId[learningId]) return null
    const cached = annotationsByLearningId[learningId]
    if (cached?.id) return cached
    const ann = await fetchLearningAnnotationForLearning(projectId, learningId)
    if (!ann) {
      set((state) => ({
        annotationAbsentByLearningId: {
          ...state.annotationAbsentByLearningId,
          [learningId]: true,
        },
      }))
      return null
    }
    get().mergeLearningAnnotationForLearning(learningId, ann)
    return ann
  },

  // Fetch learnings for a project (with per-project deduplication)
  fetchLearnings: async (projectId: string) => {
    const state = get()

    if (state.loadingByProject[projectId]) {
      return
    }

    set((state) => {
      if (state.loadingByProject[projectId]) {
        return state
      }
      return {
        loadingByProject: { ...state.loadingByProject, [projectId]: true },
        errorByProject: { ...state.errorByProject, [projectId]: null },
      }
    })

    const currentState = get()
    if (currentState.loadingByProject[projectId] === false) {
      return
    }

    try {
      const learningsData = await fetchLearningsService(projectId)
      set((state) => {
        const existingIds = new Set(state.learnings.map(l => l.id))
        const newLearnings = learningsData.filter(l => !existingIds.has(l.id))
        return {
          learnings: [...state.learnings, ...newLearnings],
          learningsByProject: { ...state.learningsByProject, [projectId]: learningsData },
          loadingByProject: { ...state.loadingByProject, [projectId]: false },
          errorByProject: { ...state.errorByProject, [projectId]: null },
          loading: false,
          error: null,
          isInitialized: true,
        }
      })
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to fetch learnings'
      set((state) => ({
        loadingByProject: { ...state.loadingByProject, [projectId]: false },
        errorByProject: { ...state.errorByProject, [projectId]: message },
        loading: false,
        error: message,
      }))
    }
  },
  
  // Fetch learnings for an experiment
  fetchLearningsByExperiment: async (projectId: string, experimentId: string) => {
    const state = get()
    
    if (state.loading) {
      return
    }
    
    set({ loading: true, error: null })
    
    try {
      const learningsData = await fetchLearningsByExperimentService(projectId, experimentId)
      set((state) => {
        // Merge with existing learnings, avoiding duplicates
        const existingIds = new Set(state.learnings.map(l => l.id))
        const newLearnings = learningsData.filter(l => !existingIds.has(l.id))
        return {
          learnings: [...state.learnings, ...newLearnings],
          loading: false,
          error: null,
          isInitialized: true,
        }
      })
    } catch (error: any) {
      set({ 
        error: error.message || 'Failed to fetch learnings for experiment', 
        loading: false 
      })
    }
  },
  
  // Fetch a single learning by ID
  fetchLearning: async (projectId: string, learningId: string) => {
    const state = get()
    
    const existing = state.learnings.find(l => l.id === learningId)
    if (existing) {
      return existing
    }
    
    set({ loading: true, error: null })
    
    try {
      const learning = await fetchLearningService(projectId, learningId)
      set((state) => {
        // Add to store if not already present
        const exists = state.learnings.some(l => l.id === learning.id)
        return {
          learnings: exists ? state.learnings : [...state.learnings, learning],
          loading: false,
          error: null,
        }
      })
      return learning
    } catch (error: any) {
      set({ 
        error: error.message || 'Failed to fetch learning', 
        loading: false 
      })
      return null
    }
  },
  
  // Add a learning to the store
  addLearning: (learning) => {
    set((state) => {
      // Don't add if already exists
      if (state.learnings.some(l => l.id === learning.id)) {
        return state
      }
      return { 
        learnings: [...state.learnings, learning] 
      }
    })
  },
  
  // Update a learning in the store
  updateLearning: (learningId, updates) => {
    set((state) => ({
      learnings: state.learnings.map(learning => 
        learning.id === learningId 
          ? { ...learning, ...updates }
          : learning
      )
    }))
  },
  
  // Remove a learning from the store
  removeLearning: (learningId) => {
    set((state) => {
      const nextAnn = { ...state.annotationsByLearningId }
      delete nextAnn[learningId]
      const nextAbsent = { ...state.annotationAbsentByLearningId }
      delete nextAbsent[learningId]
      return {
        learnings: state.learnings.filter((learning) => learning.id !== learningId),
        annotationsByLearningId: nextAnn,
        annotationAbsentByLearningId: nextAbsent,
      }
    })
  },
  
  // Update workflow status on the learning via JSON Patch /status
  updateLearningStatus: async (projectId: string, learningId: string, status: LearningStatus) => {
    set({ loading: true, error: null })

    try {
      const updated = await patchLearningApi(projectId, learningId, [
        { op: 'replace', path: '/status', value: status },
      ])
      const prev = get().getLearningById(learningId)
      const merged =
        prev?.annotation && !updated.annotation
          ? { ...updated, annotation: prev.annotation }
          : updated

      set((state) => {
        const inGlobal = state.learnings.some((l) => l.id === learningId)
        const projectList = state.learningsByProject[projectId]
        const inProject = projectList?.some((l) => l.id === learningId)

        return {
          learnings: inGlobal
            ? mapLearningsWithMerged(state.learnings, learningId, merged)
            : state.learnings,
          learningsByProject:
            projectList && inProject
              ? {
                  ...state.learningsByProject,
                  [projectId]: mapLearningsWithMerged(projectList, learningId, merged),
                }
              : state.learningsByProject,
          loading: false,
        }
      })
      void useProjectDashboardStore.getState().refreshProjectDashboard(projectId, {
        reloadAnalytics: false,
      })
      notifyWorkflowSnapshotStale({
        projectId,
        entityTypesAffected: ['learning'],
        source: 'learning_status',
      })
      return merged
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Failed to update learning status'
      set({
        error: message,
        loading: false,
      })
      throw error
    }
  },
  
  // Delete a learning
  deleteLearning: async (projectId: string, learningId: string) => {
    set({ loading: true, error: null })
    
    try {
      await deleteLearningService(projectId, learningId)
      set((state) => {
        const nextAnn = { ...state.annotationsByLearningId }
        delete nextAnn[learningId]
        const nextAbsent = { ...state.annotationAbsentByLearningId }
        delete nextAbsent[learningId]
        return {
          learnings: state.learnings.filter((learning) => learning.id !== learningId),
          annotationsByLearningId: nextAnn,
          annotationAbsentByLearningId: nextAbsent,
        }
      })
      set({ loading: false })
    } catch (error: any) {
      set({ 
        error: error.message || 'Failed to delete learning', 
        loading: false 
      })
      throw error
    }
  },
  
  // Clear all learning data
  clearAll: () => {
    set({
      learnings: [],
      loading: false,
      error: null,
      isInitialized: false,
      learningsByProject: {},
      loadingByProject: {},
      errorByProject: {},
      annotationsByLearningId: {},
      annotationAbsentByLearningId: {},
    })
  },
  
  // Get a learning by ID
  getLearningById: (id: string) => {
    return get().learnings.find(learning => learning.id === id)
  },
  
  // Get learnings by experiment ID
  getLearningsByExperiment: (experimentId: string) => {
    return get().learnings.filter(learning => learning.experimentId === experimentId)
  },
  
  // Get learnings by project ID (from project-scoped cache after fetchLearnings)
  getLearningsByProject: (projectId: string) => {
    return get().learningsByProject[projectId] || []
  },
}))
